import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ITag } from '@koomzo/coremodel';
import { BaseFormControlModel } from '@koomzo/dynamicforms';
import { BaseAdminComponent } from '@shared/components/core/base-admin.component';
import { TagDbService } from '@shared/services/index';
import {
  BulkAction,
  BulkActionEvent,
  KzListController,
  KzListDetailComponent,
  TableColumn,
} from '@shared/kz-ui';

@Component({
    selector: 'tag-admin',
    templateUrl: './tag-admin.component.html',
    styleUrls: ['./tag-admin.component.scss'],
    standalone: false
})
export class TagAdminComponent extends BaseAdminComponent<ITag> implements OnInit {
  /** Client-side search/sort/pagination over the tags list (design list behavior). */
  readonly list = new KzListController<ITag>({
    searchFields: ['name'],
    pageSize: 20,
    valueAccessor: (tag, key) => this.tagValue(tag, key),
  });
  loading = true;

  /** The list-detail shell instance — used to clear row selection after a bulk op. */
  @ViewChild(KzListDetailComponent) private listDetail?: KzListDetailComponent<ITag>;

  constructor(public db: TagDbService, private destroyRef: DestroyRef) {
    super();
    this.useDrawerForm = true;
    this.formModel = this.getFormModel();
  }

  ngOnInit() {
    this.title = this.languageService.get('tags.adminTitle');
    this.modelList$ = this.db.get();
    this.modelList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(items => {
      this.list.setItems(items ?? []);
      this.loading = false;
    });
  }

  /** Data-table columns (labels resolve to the active language on each CD). */
  get columns(): TableColumn[] {
    return [
      { key: 'name', label: this.languageService.get('tags.name'), sortable: true, size: '10' },
      { key: 'actions', label: this.languageService.get('tags.actions'), align: 'end', size: '2' },
    ];
  }

  /** Resolve an ILocaleProp field (name) to the active language for search/sort. */
  private tagValue(tag: ITag, key: string): unknown {
    const v = (tag as Record<string, any>)[key];
    if (v && typeof v === 'object') {
      const lang = this.languageService.activeLang || 'en';
      return v[lang] ?? v['en'] ?? Object.values(v)[0] ?? '';
    }
    return v;
  }

  /** Batch actions offered when one or more rows are selected. */
  get bulkActions(): BulkAction[] {
    return [
      {
        id: 'delete',
        label: this.languageService.get('uiString.delete'),
        icon: 'trash-outline',
        color: 'danger',
      },
    ];
  }

  /** Route a bulk action from the list-detail shell to the db layer. */
  async onBulkAction(event: BulkActionEvent<ITag>): Promise<void> {
    if (event.action.id !== 'delete' || !event.rows.length) {
      return;
    }
    const canDelete: boolean = await this.alertService.presentAlertWithCallback(
      this.languageService.get('uiString.confirmAction'),
      this.languageService.get('uiString.dataCannotBeRecoveredNote'),
    );
    if (canDelete) {
      event.rows.forEach((tag) => this.db.delete(tag.id));
      this.listDetail?.clearSelection();
    }
  }

  getFormModel(): BaseFormControlModel[] {
    const model: BaseFormControlModel[] = [
      this.dynamicFormControlModelFactory.getLocalePropFormModel({
        id: 'name',
        label: 'Tag name',
        layout: { row: 1, col: 1 }
      })
    ];
    return model;
  }
}
